import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from 'react-query';
import { api } from '../services/api';
import { Users, Link2, Mail, X, Clock, Shield, Eye, Edit, Trash2, Copy, CheckCircle, XCircle } from 'lucide-react';
import toast from 'react-hot-toast';
import './ShareManagement.css';

interface ShareManagementProps {
  credentialId?: string;
  kubeconfigId?: string;
  vpnId?: string;
  resourceType?: 'credential' | 'kubeconfig' | 'vpn';
  onClose?: () => void;
}

interface UserAccess {
  id: string;
  type: 'user';
  user: {
    id: string;
    username: string;
    email: string;
    firstName?: string;
    lastName?: string;
    role: string;
  };
  permissions: {
    canView: boolean;
    canEdit: boolean;
    canDelete: boolean;
  };
  expiresAt?: string;
  createdAt: string;
  updatedAt: string;
}

interface ShareLink {
  id: string;
  type: 'link';
  shareType: 'link' | 'email';
  email?: string;
  shareToken: string;
  isOneTimeUse: boolean;
  isUsed: boolean;
  expiresAt: string;
  createdAt: string;
  createdBy: {
    id: string;
    username: string;
    email: string;
  };
  shareUrl: string;
  hasPassword: boolean;
}

const ShareManagement: React.FC<ShareManagementProps> = ({ 
  credentialId, 
  kubeconfigId,
  vpnId,
  resourceType = credentialId ? 'credential' : (kubeconfigId ? 'kubeconfig' : 'vpn'),
  onClose 
}) => {
  const queryClient = useQueryClient();
  const [copiedToken, setCopiedToken] = useState<string | null>(null);
  const resourceId = credentialId || kubeconfigId || vpnId || '';

  const { data, isLoading, refetch } = useQuery(
    [`${resourceType}-shares`, resourceId],
    async () => {
      let endpoint;
      if (resourceType === 'credential') {
        endpoint = `/credentials/${resourceId}/shares`;
      } else if (resourceType === 'kubeconfig') {
        endpoint = `/kubeconfig/${resourceId}/shares`;
      } else {
        endpoint = `/vpn/${resourceId}/shares`;
      }
      const response = await api.get(endpoint);
      return response.data;
    },
    { enabled: !!resourceId }
  );

  const removeAccessMutation = useMutation(
    async (accessId: string) => {
      let endpoint;
      if (resourceType === 'credential') {
        endpoint = `/credentials/${resourceId}/access/${accessId}`;
      } else if (resourceType === 'kubeconfig') {
        endpoint = `/kubeconfig/${resourceId}/access/${accessId}`;
      } else {
        endpoint = `/vpn/${resourceId}/access/${accessId}`;
      }
      await api.delete(endpoint);
    },
    {
      onSuccess: () => {
        toast.success('Access removed successfully');
        refetch();
        const queryKey = resourceType === 'credential' ? ['credential', resourceId] : (resourceType === 'kubeconfig' ? ['kubeconfig', resourceId] : ['vpn', resourceId]);
        queryClient.invalidateQueries(queryKey);
      },
      onError: (error: any) => {
        toast.error(error.response?.data?.error || 'Failed to remove access');
      },
    }
  );

  const revokeShareMutation = useMutation(
    async (shareId: string) => {
      // The unified endpoint handles credential, kubeconfig, and VPN shares
      if (resourceType === 'vpn') {
        await api.delete(`/shares/vpn/share/${shareId}`);
      } else {
        await api.delete(`/shares/shares/${shareId}`);
      }
    },
    {
      onSuccess: () => {
        toast.success('Share link revoked successfully');
        refetch();
        const queryKey = resourceType === 'credential' ? ['credential', resourceId] : ['kubeconfig', resourceId];
        queryClient.invalidateQueries(queryKey);
      },
      onError: (error: any) => {
        toast.error(error.response?.data?.error || 'Failed to revoke share');
      },
    }
  );

  const handleRemoveAccess = (accessId: string, username: string) => {
    if (window.confirm(`Are you sure you want to remove access for ${username}?`)) {
      removeAccessMutation.mutate(accessId);
    }
  };

  const handleRevokeShare = (shareId: string, email?: string) => {
    const target = email || 'this share link';
    if (window.confirm(`Are you sure you want to revoke ${target}?`)) {
      revokeShareMutation.mutate(shareId);
    }
  };

  const handleCopyToken = async (token: string) => {
    try {
      await navigator.clipboard.writeText(token);
      setCopiedToken(token);
      toast.success('Share token copied to clipboard');
      setTimeout(() => setCopiedToken(null), 2000);
    } catch (error) {
      toast.error('Failed to copy token');
    }
  };

  const formatDate = (dateString?: string | null) => {
    if (!dateString) return 'Never';
    return new Date(dateString).toLocaleString();
  };

  const isExpired = (expiresAt?: string | null) => {
    if (!expiresAt) return false; // null means never expires
    return new Date(expiresAt) < new Date();
  };

  const userAccesses: UserAccess[] = data?.userAccesses || [];
  const shareLinks: ShareLink[] = data?.shareLinks || [];

  if (isLoading) {
    return (
      <div className="share-management">
        <div className="loading">Loading shares...</div>
      </div>
    );
  }

  return (
    <div className="share-management">
      <div className="share-management-header">
        <h2>Manage Shares</h2>
        {onClose && (
          <button className="close-btn" onClick={onClose}>
            <X size={20} />
          </button>
        )}
      </div>

      <div className="share-sections">
        {/* User Access Section */}
        <div className="share-section">
          <div className="section-header">
            <Users size={20} />
            <h3>Shared with Users ({userAccesses.length})</h3>
          </div>

          {userAccesses.length === 0 ? (
            <div className="empty-state">No users have access to this {resourceType}</div>
          ) : (
            <div className="share-list">
              {userAccesses.map((access) => (
                <div key={access.id} className="share-item">
                  <div className="share-item-header">
                    <div className="share-item-info">
                      <div className="share-item-title">
                        <strong>{access.user.username}</strong>
                        {access.user.firstName || access.user.lastName && (
                          <span className="share-item-subtitle">
                            {[access.user.firstName, access.user.lastName].filter(Boolean).join(' ')}
                          </span>
                        )}
                      </div>
                      <div className="share-item-meta">
                        <span className="meta-item">{access.user.email}</span>
                        <span className="meta-item badge">{access.user.role}</span>
                      </div>
                    </div>
                    <button
                      className="remove-btn"
                      onClick={() => handleRemoveAccess(access.id, access.user.username)}
                      disabled={removeAccessMutation.isLoading}
                      title="Remove access"
                    >
                      <X size={16} />
                    </button>
                  </div>

                  <div className="share-item-details">
                    <div className="permissions">
                      <span className={`permission-badge ${access.permissions.canView ? 'active' : ''}`}>
                        <Eye size={12} />
                        View
                      </span>
                      <span className={`permission-badge ${access.permissions.canEdit ? 'active' : ''}`}>
                        <Edit size={12} />
                        Edit
                      </span>
                      <span className={`permission-badge ${access.permissions.canDelete ? 'active' : ''}`}>
                        <Trash2 size={12} />
                        Delete
                      </span>
                    </div>
                    <div className="share-item-meta">
                      <span className="meta-item">
                        <Clock size={12} />
                        Shared: {formatDate(access.createdAt)}
                      </span>
                      {access.expiresAt && (
                        <span className={`meta-item ${isExpired(access.expiresAt) ? 'expired' : ''}`}>
                          {isExpired(access.expiresAt) ? (
                            <>
                              <XCircle size={12} />
                              Expired: {formatDate(access.expiresAt)}
                            </>
                          ) : (
                            <>
                              <Clock size={12} />
                              Expires: {formatDate(access.expiresAt)}
                            </>
                          )}
                        </span>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Share Links Section */}
        <div className="share-section">
          <div className="section-header">
            <Link2 size={20} />
            <h3>Share Links ({shareLinks.length})</h3>
          </div>

          {shareLinks.length === 0 ? (
            <div className="empty-state">No share links created for this {resourceType}</div>
          ) : (
            <div className="share-list">
              {shareLinks.map((share) => (
                <div key={share.id} className="share-item">
                  <div className="share-item-header">
                    <div className="share-item-info">
                      <div className="share-item-title">
                        {share.shareType === 'email' ? (
                          <>
                            <Mail size={16} />
                            <strong>{share.email || 'Email Share'}</strong>
                          </>
                        ) : (
                          <>
                            <Link2 size={16} />
                            <strong>Direct Link</strong>
                          </>
                        )}
                      </div>
                      <div className="share-item-meta">
                        <span className="meta-item">
                          Created by: {share.createdBy.username}
                        </span>
                        <span className="meta-item">
                          {formatDate(share.createdAt)}
                        </span>
                      </div>
                    </div>
                    <button
                      className="remove-btn"
                      onClick={() => handleRevokeShare(share.id, share.email)}
                      disabled={revokeShareMutation.isLoading}
                      title="Revoke share"
                    >
                      <X size={16} />
                    </button>
                  </div>

                  <div className="share-item-details">
                    <div className="share-badges">
                      {share.isOneTimeUse && (
                        <span className="badge warning">
                          <Shield size={12} />
                          One-time use
                        </span>
                      )}
                      {share.isUsed && (
                        <span className="badge used">
                          <CheckCircle size={12} />
                          Used
                        </span>
                      )}
                      {!share.isUsed && !isExpired(share.expiresAt) && (
                        <span className="badge active">
                          <CheckCircle size={12} />
                          Active
                        </span>
                      )}
                      {isExpired(share.expiresAt) && (
                        <span className="badge expired">
                          <XCircle size={12} />
                          Expired
                        </span>
                      )}
                      {share.hasPassword && (
                        <span className="badge">
                          <Shield size={12} />
                          Password protected
                        </span>
                      )}
                    </div>
                    <div className="share-link-info">
                      <div className="share-url">
                        <span className="url-text">{share.shareUrl}</span>
                        <button
                          className="copy-btn"
                          onClick={() => handleCopyToken(share.shareToken)}
                          title="Copy share token"
                        >
                          {copiedToken === share.shareToken ? (
                            <CheckCircle size={14} />
                          ) : (
                            <Copy size={14} />
                          )}
                        </button>
                      </div>
                      <div className="share-item-meta">
                        <span className="meta-item">
                          <Clock size={12} />
                          Expires: {formatDate(share.expiresAt)}
                        </span>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default ShareManagement;

